// Keybinds (KeyboardEvent.code, "Mouse0".."Mouse4", "WheelUp"/"WheelDown") -> `Buttons` bits and
// the non-button game actions (weapon slots, scoreboard, chat).

import { Buttons, FREEZE_LOCKED } from "../wasm/types";
import type { InputCmdData } from "../wasm/types";
import type { Action, Binding } from "../settings/schema";

export type KeybindsLike = Readonly<Record<Action, readonly Binding[]>>;

/** Actions held as a bit of `InputCmd.buttons`. */
export const BUTTON_ACTIONS: readonly (readonly [Action, number])[] = [
  ["forward", Buttons.FORWARD],
  ["back", Buttons.BACK],
  ["left", Buttons.LEFT],
  ["right", Buttons.RIGHT],
  ["jump", Buttons.JUMP],
  ["crouch", Buttons.CROUCH],
  ["walk", Buttons.WALK],
  ["fire", Buttons.FIRE],
  ["ads", Buttons.ADS],
  ["reload", Buttons.RELOAD],
  ["interact", Buttons.INTERACT],
  ["melee", Buttons.MELEE],
  ["lethal", Buttons.LETHAL],
  ["tactical", Buttons.TACTICAL],
  ["dropBomb", Buttons.DROP_BOMB],
];

/** Actions that are handled on the key-down edge instead of being sampled every tick. */
export type EdgeAction =
  | "primary"
  | "secondary"
  | "knife"
  | "nextWeapon"
  | "prevWeapon"
  | "lastWeapon"
  | "scoreboard"
  | "chat"
  | "teamChat"
  | "lobby";

export const EDGE_ACTIONS: readonly EdgeAction[] = [
  "primary",
  "secondary",
  "knife",
  "nextWeapon",
  "prevWeapon",
  "lastWeapon",
  "scoreboard",
  "chat",
  "teamChat",
  "lobby",
];

/** Binding -> actions lookup built once per settings change (no per-event allocation). */
export class Keymap {
  private readonly byBinding = new Map<Binding, Action[]>();

  constructor(keybinds: KeybindsLike) {
    for (const action of Object.keys(keybinds) as Action[]) {
      for (const b of keybinds[action]) {
        const list = this.byBinding.get(b);
        if (list) {
          if (!list.includes(action)) list.push(action);
        } else {
          this.byBinding.set(b, [action]);
        }
      }
    }
  }

  /** Actions bound to `binding` (empty when none). */
  actions(binding: Binding): readonly Action[] {
    return this.byBinding.get(binding) ?? EMPTY;
  }
}

const EMPTY: readonly Action[] = [];

/** True when any binding of `action` is in `held`. */
export function actionHeld(held: ReadonlySet<Binding>, keybinds: KeybindsLike, action: Action): boolean {
  for (const b of keybinds[action]) {
    if (held.has(b)) return true;
  }
  return false;
}

/** Toggle-mode overrides: when defined, the bit follows this value instead of the held keys. */
export interface ButtonOverrides {
  ads?: boolean;
  crouch?: boolean;
}

/** `Buttons` mask for the keys currently held. */
export function buttonMask(held: ReadonlySet<Binding>, keybinds: KeybindsLike, overrides?: ButtonOverrides): number {
  let mask = 0;
  for (const [action, bit] of BUTTON_ACTIONS) {
    let on: boolean;
    if (action === "ads" && overrides?.ads !== undefined) on = overrides.ads;
    else if (action === "crouch" && overrides?.crouch !== undefined) on = overrides.crouch;
    else on = actionHeld(held, keybinds, action);
    if (on) mask |= bit;
  }
  return mask;
}

/** Clears what the server ignores during the freeze time of a Defusal round from `cmd` (in place). */
export function freezeInput(cmd: InputCmdData): void {
  cmd.buttons &= ~FREEZE_LOCKED;
  cmd.fire = undefined;
}

/** The mouse `binding` name of `MouseEvent.button`. */
export function mouseBinding(button: number): Binding {
  return `Mouse${button}`;
}

/** Wheel direction binding for a `WheelEvent.deltaY`, null for a zero delta. */
export function wheelBinding(deltaY: number): Binding | null {
  if (deltaY < 0) return "WheelUp";
  if (deltaY > 0) return "WheelDown";
  return null;
}
